import React, { useState, useEffect } from 'react';
import DeviceSelector from './DeviceSelector';
import DeviceStatus from './DeviceStatus';
import DeviceQueue from './DeviceQueue';
import DeviceHealthChart from './DeviceHealthChart';
import DeviceThermals from './DeviceThermals';
import DeviceSettings from './DeviceSettings';
import DeviceOverview from './DeviceOverview';
import DeviceMedia from './DeviceMedia';
import { BarChart3, Thermometer, ClipboardList, Settings, ChevronLeft, RefreshCw, Film } from './Icons';
import { 
  fetchDevices, 
  fetchDeviceStatus, 
  fetchDeviceQueue, 
  fetchDeviceHealthLogs,
  fetchDeviceConfig,
  updateDeviceConfig,
  fetchGates,
  fetchGateAssignments,
  fetchLocations
} from '../services/api';
import './DevicesPage.css';

const DevicesPage = ({ initialSelectedDevice }) => {
  const [devices, setDevices] = useState([]);
  const [selectedDevice, setSelectedDevice] = useState(initialSelectedDevice || null);
  const [activeSubTab, setActiveSubTab] = useState('overview');
  
  // Data states
  const [deviceStatus, setDeviceStatus] = useState(null);
  const [deviceQueue, setDeviceQueue] = useState(null);
  const [healthLogs, setHealthLogs] = useState([]);
  const [deviceConfig, setDeviceConfig] = useState(null);
  const [allGates, setAllGates] = useState([]);
  const [gateAssignments, setGateAssignments] = useState({});
  const [locations, setLocations] = useState([]);

  // Health logs date range filter
  const [healthLogsDays, setHealthLogsDays] = useState(7); // Default to last 7 days
  const [logsQueried, setLogsQueried] = useState(false); // Track if user has requested logs
  const [logsLoading, setLogsLoading] = useState(false);

  // Cache for device-specific data to avoid re-querying
  const deviceDataCacheRef = React.useRef({});
  
  // Gate coordinates updated callback
  const handleGateCoordinatesUpdated = async () => {
    try {
      const gatesData = await fetchGates();
      setAllGates(gatesData);
    } catch (err) {
      console.error('Error refreshing gates:', err);
    }
  };

  // Loading states
  const [loading, setLoading] = useState(false);
  const [refreshingQueue, setRefreshingQueue] = useState(false);
  const [savingConfig, setSavingConfig] = useState(false);
  const [error, setError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Load devices, gates, gate assignments, and locations on mount
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        const [devicesList, gatesData, assignments] = await Promise.all([
          fetchDevices(),
          fetchGates(),
          fetchGateAssignments()
        ]);
        setDevices(devicesList);
        setAllGates(gatesData);
        setGateAssignments(assignments);
      } catch (err) {
        console.error('Error loading initial data:', err);
        setError('Failed to load devices: ' + err.message);
      }

      // Fetch locations separately so a failure doesn't block the whole page
      try {
        const locationsData = await fetchLocations();
        setLocations(locationsData);
      } catch (err) {
        console.warn('Could not load locations (endpoint may not be deployed yet):', err.message);
      }
    };

    loadInitialData();
  }, []);

  // Update selected device when initialSelectedDevice prop changes
  useEffect(() => {
    if (initialSelectedDevice && initialSelectedDevice !== selectedDevice) {
      setSelectedDevice(initialSelectedDevice);
      setActiveSubTab('overview');
    }
  }, [initialSelectedDevice, selectedDevice]);

  // Load device data when device is selected (but NOT logs - only on demand)
  useEffect(() => {
    if (!selectedDevice) {
      setDeviceStatus(null);
      setDeviceQueue(null);
      setHealthLogs([]);
      setDeviceConfig(null);
      setLogsQueried(false);
      return;
    }

    const loadDeviceData = async () => {
      // Check if we have cached data for this device (excluding logs)
      const cache = deviceDataCacheRef.current[selectedDevice];
      if (cache && !cache.needsRefresh) {
        // Use cached data
        setDeviceStatus(cache.status);
        setDeviceQueue(cache.queue);
        setDeviceConfig(cache.config);
        setLogsQueried(cache.logsQueried);
        if (cache.logsQueried) {
          setHealthLogs(cache.logs);
        }
        return;
      }

      setLoading(true);
      setError(null);

      try {
        // Fetch device data WITHOUT logs (logs are loaded on demand)
        const [status, queue, config] = await Promise.all([
          fetchDeviceStatus(selectedDevice),
          fetchDeviceQueue(selectedDevice),
          fetchDeviceConfig(selectedDevice)
        ]);

        setDeviceStatus(status);
        setDeviceQueue(queue);
        setDeviceConfig(config);

        // Cache the data
        deviceDataCacheRef.current[selectedDevice] = {
          status,
          queue,
          config,
          logs: [],
          logsQueried: false,
          needsRefresh: false,
          cachedAt: Date.now()
        };
      } catch (err) {
        console.error('Error loading device data:', err);
        setError('Failed to load device data: ' + err.message);
      } finally {
        setLoading(false);
      }
    };

    loadDeviceData();
  }, [selectedDevice]);

  const handleSelectDevice = (deviceId) => {
    setSelectedDevice(deviceId || null);
    setActiveSubTab('overview'); // Reset to overview when changing devices
    setSaveSuccess(false);
  };

  const handleRefreshQueue = async () => {
    if (!selectedDevice) return;
    
    setRefreshingQueue(true);
    try {
      const queue = await fetchDeviceQueue(selectedDevice);
      setDeviceQueue(queue);
    } catch (err) {
      console.error('Error refreshing queue:', err);
      setError('Failed to refresh queue: ' + err.message);
    } finally {
      setRefreshingQueue(false);
    }
  };

  const handleSaveConfig = async (updates) => {
    if (!selectedDevice) return;

    setSavingConfig(true);
    setSaveSuccess(false);
    setError(null);

    try {
      const updatedConfig = await updateDeviceConfig(selectedDevice, updates);
      setDeviceConfig(updatedConfig);
      setSaveSuccess(true);
      // Clear success message after 3 seconds
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      console.error('Error saving config:', err);
      setError('Failed to save configuration: ' + err.message);
    } finally {
      setSavingConfig(false);
    }
  };

  const handleRefreshDeviceData = () => {
    if (!selectedDevice) return;
    // Clear cache for this device to force re-query
    deviceDataCacheRef.current[selectedDevice].needsRefresh = true;
    // Trigger re-fetch by simulating device selection change
    setSelectedDevice(null);
    setTimeout(() => setSelectedDevice(selectedDevice), 0);
  };

  const handleQueryLogs = async () => {
    if (!selectedDevice) return;

    setLogsLoading(true);
    setError(null);

    try {
      // Calculate the date limit for health logs
      const daysAgo = new Date();
      daysAgo.setDate(daysAgo.getDate() - healthLogsDays);
      const limitTimestamp = Math.floor(daysAgo.getTime() / 1000);

      const logs = await fetchDeviceHealthLogs(selectedDevice, 1000, limitTimestamp);
      setHealthLogs(logs);
      setLogsQueried(true);

      // Update cache
      if (deviceDataCacheRef.current[selectedDevice]) {
        deviceDataCacheRef.current[selectedDevice].logs = logs;
        deviceDataCacheRef.current[selectedDevice].logsQueried = true;
        deviceDataCacheRef.current[selectedDevice].daysQueried = healthLogsDays;
      }
    } catch (err) {
      console.error('Error loading health logs:', err);
      setError('Failed to load health logs: ' + err.message);
    } finally {
      setLogsLoading(false);
    }
  };

  const renderSubTabContent = () => {
    switch (activeSubTab) {
      case 'overview':
        return (
          <div className="subtab-content overview-content">
            <DeviceStatus status={deviceStatus} />

            {!logsQueried ? (
              <div className="logs-prompt">
                <div className="logs-prompt-content">
                  <p>Load health logs to view device history and analytics.</p>
                  <div className="logs-prompt-controls">
                    <div className="filter-buttons">
                      {[7, 14, 30, 60, 90].map(days => (
                        <button
                          key={days}
                          className={`filter-btn ${healthLogsDays === days ? 'active' : ''}`}
                          onClick={() => setHealthLogsDays(days)}
                        >
                          {days}d
                        </button>
                      ))}
                    </div>
                    <div className="filter-input">
                      <input
                        type="number"
                        min="1"
                        max="365"
                        value={healthLogsDays}
                        onChange={(e) => setHealthLogsDays(Math.max(1, parseInt(e.target.value) || 1))}
                        placeholder="Custom days"
                        title="Enter custom number of days"
                      />
                    </div>
                    <button
                      className="query-logs-btn"
                      onClick={handleQueryLogs}
                      disabled={logsLoading}
                    >
                      {logsLoading ? 'Loading...' : `Query Logs (${healthLogsDays}d)`}
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <>
                <div className="health-logs-filter">
                  <label>Health Logs Time Range:</label>
                  <div className="filter-buttons">
                    {[7, 14, 30, 60, 90].map(days => (
                      <button
                        key={days}
                        className={`filter-btn ${healthLogsDays === days ? 'active' : ''}`}
                        onClick={() => setHealthLogsDays(days)}
                        disabled={logsLoading}
                      >
                        {days}d
                      </button>
                    ))}
                  </div>
                  <div className="filter-input">
                    <input
                      type="number"
                      min="1"
                      max="365"
                      value={healthLogsDays}
                      onChange={(e) => setHealthLogsDays(Math.max(1, parseInt(e.target.value) || 1))}
                      placeholder="Custom days"
                      title="Enter custom number of days"
                      disabled={logsLoading}
                    />
                  </div>
                  <button
                    className="requery-logs-btn"
                    onClick={handleQueryLogs}
                    disabled={logsLoading}
                    title="Re-query logs with current date range"
                  >
                    {logsLoading ? 'Loading...' : 'Refresh'}
                  </button>
                </div>
                <DeviceHealthChart healthLogs={healthLogs} deviceId={selectedDevice} />
              </>
            )}
          </div>
        );
      
      case 'thermals':
        return (
          <div className="subtab-content thermals-content">
            <DeviceThermals healthLogs={healthLogs} deviceId={selectedDevice} />
          </div>
        );

      case 'queue':
        return (
          <div className="subtab-content queue-content">
            <DeviceQueue 
              queue={deviceQueue} 
              onRefresh={handleRefreshQueue}
              refreshing={refreshingQueue}
            />
          </div>
        );
      
      case 'media':
        return (
          <div className="subtab-content media-content">
            <DeviceMedia
              key={selectedDevice}
              deviceId={selectedDevice}
              capturePointId={deviceConfig?.capturePoint_id}
            />
          </div>
        );

      case 'settings':
        return (
          <div className="subtab-content settings-content">
            {saveSuccess && (
              <div className="save-success-message">
                Configuration saved successfully!
              </div>
            )}
            <DeviceSettings 
              config={deviceConfig}
              allGates={allGates}
              gateAssignments={gateAssignments}
              locations={locations}
              currentDeviceId={selectedDevice}
              onSave={handleSaveConfig}
              saving={savingConfig}
              onGateCoordinatesUpdated={handleGateCoordinatesUpdated}
            />
          </div>
        );
      
      default:
        return null;
    }
  };

  return (
    <div className="devices-page">
      {/* Combined device selector + subtabs bar */}
      <div className="device-toolbar">
        <DeviceSelector
          devices={devices}
          selectedDevice={selectedDevice}
          onSelectDevice={handleSelectDevice}
          loading={loading}
        />
        {selectedDevice && (
          <>
            <button
              className="back-to-overview-btn"
              onClick={() => handleSelectDevice('')}
              title="Back to device overview"
            >
              <ChevronLeft size={16} />
              <span>All Devices</span>
            </button>
            <button
              className="refresh-device-btn"
              onClick={handleRefreshDeviceData}
              title="Refresh device data and clear cache"
              disabled={loading}
            >
              <RefreshCw size={16} />
              <span>Refresh</span>
            </button>
          </>
        )}
        {selectedDevice && (
          <div className="device-subtabs">
            <button
              className={`subtab ${activeSubTab === 'overview' ? 'active' : ''}`}
              onClick={() => setActiveSubTab('overview')}
            >
              <BarChart3 size={16} />
              <span>Overview</span>
            </button>
            <button
              className={`subtab ${activeSubTab === 'thermals' ? 'active' : ''}`}
              onClick={() => setActiveSubTab('thermals')}
            >
              <Thermometer size={16} />
              <span>Thermals</span>
            </button>
            <button
              className={`subtab ${activeSubTab === 'queue' ? 'active' : ''}`}
              onClick={() => setActiveSubTab('queue')}
            >
              <ClipboardList size={16} />
              <span>Media Queue</span>
              {deviceQueue && (deviceQueue.pending?.length > 0 || deviceQueue.processing?.length > 0) && (
                <span className="queue-badge">
                  {(deviceQueue.pending?.length || 0) + (deviceQueue.processing?.length || 0)}
                </span>
              )}
            </button>
            <button
              className={`subtab ${activeSubTab === 'media' ? 'active' : ''}`}
              onClick={() => setActiveSubTab('media')}
            >
              <Film size={16} />
              <span>Media</span>
            </button>
            <button
              className={`subtab ${activeSubTab === 'settings' ? 'active' : ''}`}
              onClick={() => setActiveSubTab('settings')}
            >
              <Settings size={16} />
              <span>Settings</span>
            </button>
          </div>
        )}
      </div>

      {error && <div className="error-message">{error}</div>}

      {loading && (
        <div className="loading-overlay">
          <div className="loading-spinner">Loading device data...</div>
        </div>
      )}

      {selectedDevice && !loading && (
        <div className="device-content">
          {renderSubTabContent()}
        </div>
      )}

      {!selectedDevice && !loading && (
        <DeviceOverview
          devices={devices}
          gates={allGates}
          gateAssignments={gateAssignments}
          locations={locations}
          onSelectDevice={handleSelectDevice}
        />
      )}
    </div>
  );
};

export default DevicesPage;
